import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { authApi } from "../services/api";

const AuthContext = createContext(null);

function loadFromStorage() {
  try {
    const savedToken = localStorage.getItem("jwt_token");
    const savedUser = localStorage.getItem("jwt_user");
    const savedPermissions = localStorage.getItem("jwt_permissions");
    const savedGlobalPermissions = localStorage.getItem("jwt_global_permissions");
    if (savedToken && savedUser) {
      return {
        token: savedToken,
        user: JSON.parse(savedUser),
        permissions: savedPermissions ? JSON.parse(savedPermissions) : [],
        globalPermissions: savedGlobalPermissions ? JSON.parse(savedGlobalPermissions) : [],
      };
    }
  } catch {
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("jwt_user");
    localStorage.removeItem("jwt_permissions");
    localStorage.removeItem("jwt_global_permissions");
  }
  return { token: null, user: null, permissions: [], globalPermissions: [] };
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(loadFromStorage);

  const login = useCallback((jwtToken, userData, userPermissions = [], userGlobalPermissions = []) => {
    const newAuth = { token: jwtToken, user: userData, permissions: userPermissions, globalPermissions: userGlobalPermissions };
    setAuth(newAuth);
    localStorage.setItem("jwt_token", jwtToken);
    localStorage.setItem("jwt_user", JSON.stringify(userData));
    localStorage.setItem("jwt_permissions", JSON.stringify(userPermissions));
    localStorage.setItem("jwt_global_permissions", JSON.stringify(userGlobalPermissions));
  }, []);

  const logout = useCallback(() => {
    setAuth({ token: null, user: null, permissions: [], globalPermissions: [] });
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("jwt_user");
    localStorage.removeItem("jwt_permissions");
    localStorage.removeItem("jwt_global_permissions");
    localStorage.removeItem("google_token");
  }, []);

  // On mount, refresh permissions from server so they're never stale
  useEffect(() => {
    if (!auth.token) return;
    authApi.me()
      .then((res) => {
        const fresh = res.data.permissions;
        const freshGlobal = res.data.globalPermissions || [];
        setAuth((prev) => ({ ...prev, permissions: fresh, globalPermissions: freshGlobal }));
        localStorage.setItem("jwt_permissions", JSON.stringify(fresh));
        localStorage.setItem("jwt_global_permissions", JSON.stringify(freshGlobal));
      })
      .catch(() => { /* keep cached permissions if server unreachable */ });
  }, [auth.token]);

  // Update permissions without re-login (e.g. after editing own access)
  const updatePermissions = useCallback((newPermissions, newGlobalPermissions) => {
    setAuth((prev) => ({
      ...prev,
      permissions: newPermissions,
      globalPermissions: newGlobalPermissions !== undefined ? newGlobalPermissions : prev.globalPermissions,
    }));
    localStorage.setItem("jwt_permissions", JSON.stringify(newPermissions));
    if (newGlobalPermissions !== undefined) {
      localStorage.setItem("jwt_global_permissions", JSON.stringify(newGlobalPermissions));
    }
  }, []);

  // Get list of environments the user can access
  const getAccessibleEnvs = useCallback(() => {
    return [...new Set(auth.permissions.map((p) => p.environment))];
  }, [auth.permissions]);

  // Check if user has access to a specific environment
  const hasEnvAccess = useCallback((env) => {
    return auth.permissions.some((p) => p.environment === env);
  }, [auth.permissions]);

  // Check if user can access a specific tenant in an env
  const canAccessTenant = useCallback((env, tenantName) => {
    const envPerms = auth.permissions.filter((p) => p.environment === env);
    // Wildcard (tenant_name falsy = null/undefined/"") means access to all tenants
    if (envPerms.some((p) => !p.tenant_name)) return true;
    return envPerms.some((p) => p.tenant_name === tenantName);
  }, [auth.permissions]);

  // Check if user has a specific permission in a given env+tenant scope
  const hasPermission = useCallback((env, tenantName, permKey) => {
    const envPerms = auth.permissions.filter((p) => p.environment === env);
    if (envPerms.length === 0) return false;
    // Find matching access: specific tenant first, then wildcard (falsy = all tenants)
    let match = tenantName ? envPerms.find((p) => p.tenant_name === tenantName) : null;
    if (!match) match = envPerms.find((p) => !p.tenant_name);
    if (!match) return false;
    return match.role?.permissions?.includes(permKey) || false;
  }, [auth.permissions]);

  // Check if user has a permission in ANY environment OR as a direct global permission
  const hasAnyPermission = useCallback((permKey) => {
    if (auth.globalPermissions.includes(permKey)) return true;
    return auth.permissions.some(
      (p) => p.role?.permissions?.includes(permKey)
    );
  }, [auth.permissions, auth.globalPermissions]);

  // Check if user has a specific role (by name) in any env
  const hasRole = useCallback((roleName) => {
    return auth.permissions.some((p) => p.role?.name === roleName);
  }, [auth.permissions]);

  // Convenience: true if user is Admin in any env
  const isAdmin = useMemo(() => hasRole("Admin"), [hasRole]);

  // Get all permission keys for a given env+tenant
  const getPermissionsForEnv = useCallback((env, tenantName) => {
    const envPerms = auth.permissions.filter((p) => p.environment === env);
    let match = envPerms.find((p) => p.tenant_name === tenantName);
    if (!match) match = envPerms.find((p) => p.tenant_name === null);
    return match?.role?.permissions || [];
  }, [auth.permissions]);

  const value = useMemo(() => ({
    user: auth.user,
    token: auth.token,
    permissions: auth.permissions,
    globalPermissions: auth.globalPermissions,
    isAuthenticated: !!auth.token,
    login,
    logout,
    getAccessibleEnvs,
    hasEnvAccess,
    canAccessTenant,
    hasPermission,
    hasAnyPermission,
    hasRole,
    isAdmin,
    getPermissionsForEnv,
    updatePermissions,
  }), [auth, login, logout, getAccessibleEnvs, hasEnvAccess, canAccessTenant, hasPermission, hasAnyPermission, hasRole, isAdmin, getPermissionsForEnv, updatePermissions]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
